import { isApiError } from "@colorglo/contracts/client";
import {
  Alert,
  AlertDescription,
  Button,
  Field,
  FieldLabel,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@colorglo/ui/components";
import { FeedbackError, FeedbackInfo, FeedbackWarning, iconSize } from "@colorglo/ui/icons";
import { useId, useState } from "react";
import { toast } from "sonner";
import {
  type Reassignment,
  type ResourceView,
  useDeleteResource,
  useReassignment,
  useUpdateResource,
} from "../../api/settings.ts";
import { useLocale, usePlural, useT } from "../../lib/locale.ts";
import { formatDayHeading, formatRange, formatTime, zoned } from "../../lib/time.ts";
import { SettingsError, SettingsLoading } from "./shell.tsx";

type Job = Reassignment["jobs"][number];
type Candidate = Reassignment["candidates"][number];
type Option = Job["options"][number];
type ServiceName = { readonly id: string; readonly name: Record<string, string> };

export function RemoveResourceSheet({
  franchiseId,
  resource,
  services,
  onClose,
}: {
  readonly franchiseId: string;
  readonly resource: ResourceView;
  readonly services: readonly ServiceName[];
  readonly onClose: () => void;
}) {
  const t = useT();
  const preview = useReassignment(franchiseId, resource.id);
  const remove = useDeleteResource(franchiseId);
  const update = useUpdateResource(franchiseId);

  const [assigned, setAssigned] = useState<Record<string, string>>({});
  const [conflict, setConflict] = useState<{ appointmentId: string; name: string } | null>(null);
  const [notice, setNotice] = useState<"new_booking" | "error" | null>(null);

  const jobs = preview.data?.jobs ?? [];
  const candidates = preview.data?.candidates ?? [];
  const ready =
    preview.isSuccess && jobs.every((job) => assigned[job.appointment_id] !== undefined);

  const onRemove = () => {
    setConflict(null);
    setNotice(null);
    remove.mutate(
      {
        resourceId: resource.id,
        assignments: jobs.map((job) => ({
          appointment_id: job.appointment_id,
          resource_id: assigned[job.appointment_id] as string,
        })),
      },
      {
        onSuccess: ({ deactivated }) => {
          if (deactivated) {
            toast(t("settings.staff.deactivated_notice", { name: resource.name }));
          }
          onClose();
        },
        onError: (error) => {
          if (!isApiError(error)) {
            setNotice("error");
            return;
          }
          if (error.error.code === "RESOURCE_IN_USE") {
            setNotice("new_booking");
            void preview.refetch();
            return;
          }
          const details = error.error.details as
            | { appointment_id?: string; resource_id?: string }
            | undefined;
          const appointmentId = details?.appointment_id;
          if (error.error.code !== "SLOT_UNAVAILABLE" || appointmentId === undefined) {
            setNotice("error");
            return;
          }
          const name = candidates.find((entry) => entry.id === details?.resource_id)?.name ?? "";
          setConflict({ appointmentId, name });
          setAssigned(({ [appointmentId]: _, ...rest }) => rest);
          void preview.refetch();
        },
      },
    );
  };

  const onDeactivate = () => {
    setNotice(null);
    update.mutate(
      { resourceId: resource.id, is_active: false },
      { onSuccess: onClose, onError: () => setNotice("error") },
    );
  };

  const canDeactivate = preview.isSuccess && jobs.length === 0 && resource.is_active;

  return (
    <Sheet open onOpenChange={(next) => next || onClose()}>
      <SheetContent closeLabel={t("settings.cancel")} className="w-full sm:max-w-xl">
        <SheetHeader>
          <SheetTitle>{t("settings.staff.remove_heading", { name: resource.name })}</SheetTitle>
        </SheetHeader>

        <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto px-4">
          {preview.isPending && <SettingsLoading />}
          {preview.isError && <SettingsError onRetry={() => void preview.refetch()} />}
          {preview.isSuccess && (
            <Assignments
              name={resource.name}
              timezone={preview.data.timezone}
              jobs={jobs}
              candidates={candidates}
              services={services}
              assigned={assigned}
              onAssign={(entries) => setAssigned((current) => ({ ...current, ...entries }))}
              conflict={conflict}
              newBooking={notice === "new_booking"}
            />
          )}

          {notice === "error" && (
            <Alert variant="destructive">
              <FeedbackError aria-hidden="true" />
              <AlertDescription>{t("settings.error")}</AlertDescription>
            </Alert>
          )}

          {canDeactivate && (
            <p className="text-body-sm text-muted-foreground">
              {t("settings.staff.deactivate_hint")}
            </p>
          )}
        </div>

        <SheetFooter className="flex-row flex-wrap gap-3">
          <Button variant="destructive" onClick={onRemove} disabled={remove.isPending || !ready}>
            {jobs.length > 0
              ? t("settings.staff.remove_confirm")
              : t("settings.staff.remove_confirm_empty")}
          </Button>
          {canDeactivate && (
            <Button variant="outline" onClick={onDeactivate} disabled={update.isPending}>
              {t("settings.staff.deactivate")}
            </Button>
          )}
          <Button variant="ghost" onClick={onClose}>
            {t("settings.cancel")}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}

function Assignments({
  name,
  timezone,
  jobs,
  candidates,
  services,
  assigned,
  onAssign,
  conflict,
  newBooking,
}: {
  readonly name: string;
  readonly timezone: string;
  readonly jobs: readonly Job[];
  readonly candidates: readonly Candidate[];
  readonly services: readonly ServiceName[];
  readonly assigned: Record<string, string>;
  readonly onAssign: (entries: Record<string, string>) => void;
  readonly conflict: { appointmentId: string; name: string } | null;
  readonly newBooking: boolean;
}) {
  const t = useT();
  const plural = usePlural();
  const bulkId = useId();

  if (jobs.length === 0) {
    return <p className="text-body">{t("settings.staff.remove_none", { name })}</p>;
  }

  const intro = plural("settings.staff.remove_body", jobs.length, { name });

  if (candidates.length === 0) {
    return (
      <>
        <p className="text-body">{intro}</p>
        <Alert>
          <FeedbackInfo aria-hidden="true" />
          <AlertDescription>{t("settings.staff.remove_no_candidates")}</AlertDescription>
        </Alert>
      </>
    );
  }

  const freeJobs = (resourceId: string) =>
    jobs.filter((job) =>
      job.options.some((option) => option.resource_id === resourceId && !option.busy),
    );
  const done = jobs.filter((job) => assigned[job.appointment_id] !== undefined).length;

  return (
    <>
      <p className="text-body">
        {intro} {t("settings.staff.remove_assign_body")}
      </p>

      <Field>
        <FieldLabel htmlFor={bulkId}>{t("settings.staff.remove_assign_all")}</FieldLabel>
        <Select
          value=""
          onValueChange={(resourceId) =>
            onAssign(
              Object.fromEntries(
                freeJobs(resourceId).map((job) => [job.appointment_id, resourceId]),
              ),
            )
          }
        >
          <SelectTrigger id={bulkId} className="w-full">
            <SelectValue placeholder={t("settings.staff.remove_pick")} />
          </SelectTrigger>
          <SelectContent>
            {candidates.map((candidate) => (
              <SelectItem key={candidate.id} value={candidate.id}>
                <CandidateName name={candidate.name} color={candidate.color} />
                <span className="text-muted-foreground">
                  {t("settings.staff.remove_free_count", {
                    free: String(freeJobs(candidate.id).length),
                    count: String(jobs.length),
                  })}
                </span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </Field>

      {newBooking && (
        <Alert>
          <FeedbackInfo aria-hidden="true" />
          <AlertDescription>{t("settings.staff.remove_new_booking")}</AlertDescription>
        </Alert>
      )}

      <ul className="flex flex-col gap-3">
        {jobs.map((job) => (
          <JobCard
            key={job.appointment_id}
            job={job}
            timezone={timezone}
            candidates={candidates}
            services={services}
            choice={assigned[job.appointment_id]}
            onChoose={(resourceId) => onAssign({ [job.appointment_id]: resourceId })}
            conflictName={conflict?.appointmentId === job.appointment_id ? conflict.name : null}
          />
        ))}
      </ul>

      <p className="text-body-sm text-muted-foreground" aria-live="polite">
        {t("settings.staff.remove_progress", {
          done: String(done),
          count: String(jobs.length),
        })}
      </p>
    </>
  );
}

function JobCard({
  job,
  timezone,
  candidates,
  services,
  choice,
  onChoose,
  conflictName,
}: {
  readonly job: Job;
  readonly timezone: string;
  readonly candidates: readonly Candidate[];
  readonly services: readonly ServiceName[];
  readonly choice: string | undefined;
  readonly onChoose: (resourceId: string) => void;
  readonly conflictName: string | null;
}) {
  const t = useT();
  const locale = useLocale();

  const start = zoned(job.starts_at, timezone);
  const end = zoned(job.ends_at, timezone);
  const when = start.toPlainDate().equals(end.toPlainDate())
    ? formatRange(start, end)
    : `${formatTime(start)} – ${formatDayHeading(end.toPlainDate(), locale)} ${formatTime(end)}`;
  const names = job.service_ids
    .map((id) => services.find((service) => service.id === id))
    .map((service) => service?.name[locale] ?? service?.name.sv)
    .filter((name) => name !== undefined)
    .join(", ");
  const chosen = job.options.find((option) => option.resource_id === choice);
  const warnings = chosen === undefined ? [] : optionWarnings(chosen);

  return (
    <li className="flex flex-col gap-2 rounded-lg border border-border p-3">
      <span className="text-body font-medium">
        {formatDayHeading(start.toPlainDate(), locale)} {when}
      </span>
      <span className="text-body-sm text-muted-foreground">
        {job.customer_name ?? t("settings.staff.remove_no_customer")}
        {names !== "" && ` · ${names}`}
      </span>

      <Select value={choice ?? ""} onValueChange={onChoose}>
        <SelectTrigger className="w-full" aria-label={t("settings.staff.remove_pick")}>
          <SelectValue placeholder={t("settings.staff.remove_pick")} />
        </SelectTrigger>
        <SelectContent>
          {job.options.map((option) => {
            const candidate = candidates.find((entry) => entry.id === option.resource_id);
            if (candidate === undefined) return null;
            const labels = option.busy
              ? ["settings.staff.remove_option_busy" as const]
              : optionWarnings(option);
            return (
              <SelectItem
                key={option.resource_id}
                value={option.resource_id}
                disabled={option.busy}
              >
                <CandidateName name={candidate.name} color={candidate.color} />
                <span className={labels.length === 0 ? "text-success" : "text-muted-foreground"}>
                  {labels.length === 0
                    ? t("settings.staff.remove_option_free")
                    : labels.map((label) => t(label)).join(" · ")}
                </span>
              </SelectItem>
            );
          })}
        </SelectContent>
      </Select>

      {warnings.length > 0 && (
        <p className="flex items-center gap-2 text-body-sm text-warning">
          <FeedbackWarning {...iconSize.inline} aria-hidden="true" />
          {warnings.map((label) => t(label)).join(" · ")}
        </p>
      )}
      {conflictName !== null && (
        <p role="alert" className="flex items-center gap-2 text-body-sm text-destructive">
          <FeedbackError {...iconSize.inline} aria-hidden="true" />
          {t("settings.staff.remove_conflict", { name: conflictName })}
        </p>
      )}
    </li>
  );
}

function CandidateName({ name, color }: { readonly name: string; readonly color: string }) {
  return (
    <span className="flex items-center gap-2">
      <span
        aria-hidden="true"
        className="size-2.5 shrink-0 rounded-full"
        style={{ backgroundColor: color }}
      />
      {name}
    </span>
  );
}

function optionWarnings(option: Option) {
  return [
    ...(option.in_hours ? [] : ["settings.staff.remove_option_hours" as const]),
    ...(option.skilled ? [] : ["settings.staff.remove_option_skill" as const]),
  ];
}
